import { Block, EMAIL_MARKER } from "./types";

/**
 * The builder can only represent the [[WL-EMAIL]] markup / easy-markup family
 * (Eyebrow:/Title:/[Summary]/[Notes]/[Footer]/plain paragraphs). Legacy templates
 * are raw HTML with no block structure — feeding them to parse() produces a single
 * escaped-HTML paragraph (garbage in the canvas). Detect those so the UI can show a
 * guard + "regenerate" option instead of dumping raw HTML.
 */
export function isBuilderCompatible(content?: string): boolean {
	const c = String(content || "")
		.replace(/\r\n?/g, "\n")
		.trim();
	if (!c) return true; // empty → builder starts fresh
	if (c.startsWith(EMAIL_MARKER)) return true; // explicit builder markup
	// Block/inline HTML tags ⇒ legacy raw HTML, not builder markup.
	if (
		/<\s*\/?\s*(table|tbody|thead|tr|td|th|div|p|h[1-6]|span|img|a|body|html|center|font|br|ul|ol|li|strong|em|b|i)\b/i.test(
			c
		)
	)
		return false;
	return true; // plain text / easy markup is parseable
}

export function parse(markup: string): Block[] {
	// Normalize line endings before any processing so \r\n and \r behave identically to \n.
	let text = String(markup || "")
		.replace(/\r\n/g, "\n")
		.replace(/\r/g, "\n")
		.trim();
	if (text.startsWith(EMAIL_MARKER)) text = text.slice(EMAIL_MARKER.length).replace(/^\n/, "");

	const lines = text.split("\n");
	const blocks: Block[] = [];
	let header: Block | null = null;
	let para: string[] = [];
	// True once any non-header block (paragraph, summary, notes, footer) has been pushed.
	// After seenBody=true, Eyebrow/Title/Subtitle lines are treated as plain paragraph text.
	let seenBody = false;

	const flushPara = () => {
		const joined = para.join("\n").trim();
		if (joined) {
			blocks.push({ type: "paragraph", data: { html: joined } });
			seenBody = true;
		}
		para = [];
	};

	const ensureHeader = (): Block => {
		if (!header) {
			header = { type: "header", required: true, data: {} };
			blocks.unshift(header);
		}
		return header;
	};

	for (let i = 0; i < lines.length; i++) {
		const line = lines[i];
		const t = line.trim();

		const mEye = /^Eyebrow:\s*(.*)$/.exec(t);
		const mTitle = /^Title:\s*(.*)$/.exec(t);
		const mSub = /^Subtitle:\s*(.*)$/.exec(t);
		const mLine = /^Line:\s*(.*)$/.exec(t);
		// Only treat as header meta while no body block has been seen yet.
		if (!seenBody && (mEye || mTitle || mSub || mLine)) {
			flushPara();
			const h = ensureHeader();
			if (mEye) h.data.eyebrow = mEye[1];
			if (mTitle) h.data.title = mTitle[1];
			if (mSub) h.data.subtitle = mSub[1];
			if (mLine) {
				if (!h.data.lines) h.data.lines = [];
				h.data.lines.push(mLine[1]);
			}
			continue;
		}

		if (/^\[Summary\]$/i.test(t)) {
			flushPara();
			const rows: import("./types").SummaryRow[] = [];
			i++;
			for (; i < lines.length && !/^\[\/Summary\]$/i.test(lines[i].trim()); i++) {
				const row = lines[i].trim();
				if (!row) continue;

				// Pair-column detection: conservative — requires exactly one " | "
				// (space-pipe-space) with BOTH halves containing ": " and a non-empty label.
				// Mirrors the backend rule in emailBlockModel.js.
				const pipeIdx = row.indexOf(" | ");
				if (pipeIdx !== -1) {
					const leftPart = row.slice(0, pipeIdx).trim();
					const rightPart = row.slice(pipeIdx + 3).trim();
					const parseSide = (
						part: string
					): { label: string; value: string; emphasis: boolean | undefined } | null => {
						const colonIdx = part.indexOf(": ");
						if (colonIdx === -1) return null;
						const rawLabel = part.slice(0, colonIdx).trim();
						const value = part.slice(colonIdx + 2).trim();
						if (!rawLabel) return null;
						const emphasis = rawLabel.endsWith("!");
						const label = emphasis ? rawLabel.slice(0, -1).trim() : rawLabel;
						return { label, value, emphasis: emphasis || undefined };
					};
					const left = parseSide(leftPart);
					const right = parseSide(rightPart);
					if (left && right) {
						rows.push({
							label: left.label,
							value: left.value,
							emphasis: left.emphasis,
							label2: right.label,
							value2: right.value,
							emphasis2: right.emphasis,
						});
						continue;
					}
					// Falls through to single-row handling if either side is invalid.
				}

				const m = /^(.+?)(!)?:\s*(.*)$/.exec(row);
				if (m) {
					rows.push({ label: m[1], value: m[3], emphasis: !!m[2] || undefined });
				} else {
					// Non "Label: Value" line (e.g. "# Section Header") — preserve as raw passthrough.
					rows.push({ raw: row });
				}
			}
			blocks.push({ type: "summary", required: true, data: { rows } });
			seenBody = true;
			continue;
		}

		const mNotes = /^\[Notes:\s*(.*?)\]$/i.exec(t);
		if (mNotes) {
			flushPara();
			const items: string[] = [];
			const rows: Array<{ k: string; v: string }> = [];
			let isRows = false;
			i++;
			for (; i < lines.length && !/^\[\/Notes\]$/i.test(lines[i].trim()); i++) {
				const row = lines[i].trim();
				if (!row) continue;
				if (row.startsWith("- ")) {
					items.push(row.slice(2));
				} else {
					isRows = true;
					// Format: "k: v" — k retains its trailing colon.
					// Serialize emits `${r.k} ${r.v}` (k already ends with ":").
					// Split on first ": " so keys with spaces (e.g. "All requests from the 1st-15th:") survive.
					const sep = row.indexOf(": ");
					if (sep > 0) {
						rows.push({ k: row.slice(0, sep + 1), v: row.slice(sep + 2) });
					} else {
						rows.push({ k: row, v: "" });
					}
				}
			}
			blocks.push(
				isRows
					? { type: "notes", style: { color: "info", layout: "rows" }, data: { title: mNotes[1], rows } }
					: { type: "notes", style: { color: "neutral", layout: "list" }, data: { title: mNotes[1], items } }
			);
			seenBody = true;
			continue;
		}

		if (/^\[Footer\]$/i.test(t)) {
			flushPara();
			const fLines: string[] = [];
			i++;
			for (; i < lines.length && !/^\[\/Footer\]$/i.test(lines[i].trim()); i++) {
				const row = lines[i].trim();
				if (!row) continue;
				// Store ALL lines verbatim — no special-casing Title:/Note: so order is preserved.
				fLines.push(row);
			}
			blocks.push({ type: "footer", required: true, data: { lines: fLines } });
			seenBody = true;
			continue;
		}

		if (t === "") {
			flushPara();
			continue;
		}

		para.push(line);
	}

	flushPara();
	return blocks;
}
